import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, LowInternalLinksDetails } from "@/types/technicalAudit";

interface LowInternalLinksCardProps {
	issue: PageIssue;
}

export function LowInternalLinksCard({ issue }: LowInternalLinksCardProps) {
	const details = issue.issue_details as LowInternalLinksDetails;
	const percentage = Math.min((details.internal_link_count / details.threshold) * 100, 100);
	const isZero = details.internal_link_count === 0;

	return (
		<div className="space-y-4">
			<div className={cn(
				"flex items-start gap-3 rounded-xl p-4 border",
				isZero ? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800" : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800",
			)}>
				<div className={cn(
					"flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
					isZero ? "bg-red-100 dark:bg-red-900/40" : "bg-amber-100 dark:bg-amber-900/40",
				)}>
					<AlertTriangle className={cn("w-5 h-5", isZero ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")} />
				</div>
				<div className="flex-1">
					<h4 className={cn("font-semibold", isZero ? "text-red-900 dark:text-red-300" : "text-amber-900 dark:text-amber-300")}>
						Low Internal Links
					</h4>
					<p className={cn("text-sm mt-1", isZero ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400")}>
						This page has only {details.internal_link_count} internal links pointing to it. At least {details.threshold} inlinks are recommended for proper crawl coverage.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Internal Inlinks
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn(
							"text-2xl font-bold",
							isZero ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400",
						)}>
							{details.internal_link_count}
						</span>
						<span className="text-sm text-muted-foreground">
							/ {details.threshold} recommended
						</span>
					</div>
					<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
						<div
							className={cn(
								"h-full rounded-full transition-all",
								isZero ? "bg-red-500" : "bg-amber-500",
							)}
							style={{ width: `${percentage}%` }}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
